import { useState, useMemo, useEffect, useContext } from "react";
import Sidebar from "../components/Sidebar";
import { Link } from "react-router-dom";
import axios from "axios";
import { MemberContext } from "../context/MemberContext";
import "react-datepicker/dist/react-datepicker.css";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { 
  getAuthToken, 
  removeAdminProfileData, 
  setSelectedMemberId, 
  getSelectedMemberId 
} from "../utils/cookieUtils";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Pagination from "../components/pagination";
import EditMemberModal from "../components/modals/memberModal/EditMemberModal";
import { useLocations } from "../hooks/useLocations";
import EditMemberStatsModal from "../components/modals/memberModal/EditMemberStatsModal";
import AccessToggleModal from "../components/modals/memberModal/AccessToggleModal";
import DeleteUserModal from "../components/modals/memberModal/DeleteUserModal";
import StatusModal from "../components/modals/StatusModal";

function ViewMember() {
  const { selectedMemberId, setSelectedMemberId } = useContext(MemberContext);
  const [userProfile, setUserProfile] = useState({});
  const token = useMemo(() => getAuthToken(), []);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendanceCount, setAttendanceCount] = useState([]);
  const itemsPerPage = 8;

  // State for all locations
  const { locations, loading: locationsLoading } = useLocations(true);

  const [memberData, setMemberData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    address: "",
    dateOfBirth: null,
    profession: "",
    NIN: "",
    location_id: null,
    weight: "",
    height: "",
    bodySize: "",
  });

  const dataURLtoFile = (dataurl, filename) => {
    if (!dataurl) return null; // Handle empty or null data URL

    const arr = dataurl.split(",");
    if (arr.length < 2) return null; // Validate data URL format

    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const [profileImage, setProfileImage] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isEditModal2Open, setIsEditModal2Open] = useState(false);
  const [isDisableModalOpen, setDisableModalOpen] = useState(false);
  const [isEnableModalOpen, setEnableModalOpen] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [disableSuccess, setDisableSuccess] = useState(false);
  const [EnableSuccess, setEnableSuccess] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);
  const [edit2Success, setEdit2Success] = useState(false);
  const [loading, setLoading] = useState(true);

  const formatDate = (date) => {
    const d = new Date(date);
    let day = d.getDate();
    let month = d.getMonth() + 1;
    const year = d.getFullYear();

    if (day < 10) {
      day = "0" + day;
    }
    if (month < 10) {
      month = "0" + month;
    }

    return `${day}-${month}-${year}`;
  };

  const parseDateString = (dateString) => {
    const [day, month, year] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: "/super-admin",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }),
    [token]
  );

  const fetchMemberData = async (memberId) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.post("/get-user-details", {
        user_id: memberId,
      });
      setUserProfile(response.data.message.user);
      setSubscriptionHistory(response.data.message.user.subscription_history);
      setAttendanceHistory(response.data.message.user.attendance_history);
      setAttendanceCount(response.data.message.user.attendance_history.length);
      setMemberData({
        firstName: response.data.message.user.f_name || "",
        lastName: response.data.message.user.l_name || "",
        phoneNumber: response.data.message.user.phone || "",
        location_id: response.data.message.user.location_id || null,
        email: response.data.message.user.email || "",
        address: response.data.message.user.address || "",
        dateOfBirth: response.data.message.user.dob
          ? parseDateString(response.data.message.user.dob)
          : null,
        profession: response.data.message.user.profession || "",
        NIN: response.data.message.user.nin || "",
        weight: response.data.message.user.start_weight || "",
        height: response.data.message.user.start_height || "",
        bodySize: response.data.message.user.body_size || "",
      });
      setProfileImage(response.data.message.user.profile_image || null);
    } catch (error) {
      console.error(
        "Error fetching members:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setIsLoading(false);
      setLoading(false); // Also set subscription history loading to false
    }
  };

  useEffect(() => {
    if (selectedMemberId) {
      setSelectedMemberId(selectedMemberId);
    }
  }, [selectedMemberId]);

  useEffect(() => {
    const storedMemberId = getSelectedMemberId();
    if (storedMemberId) {
      fetchMemberData(storedMemberId);
    } else if (selectedMemberId) {
      fetchMemberData(selectedMemberId);
    }
  }, [selectedMemberId, axiosInstance]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMemberData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (date) =>
    setMemberData((prevState) => ({ ...prevState, dateOfBirth: date }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const { type: fileType, size: fileSize } = file;
    const validFileTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/gif",
      "image/svg+xml",
    ];

    if (!validFileTypes.includes(fileType)) {
      toast.error(
        "Invalid file type. Please upload an image of type: jpeg, png, jpg, gif, svg."
      );
      return;
    }

    if (fileSize > 2 * 1024 * 1024) {
      toast.error(
        "File size too large. The file must not be greater than 2 MB."
      );
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const dataURL = reader.result;
      setProfileImage(dataURL);
      setMemberData((prevState) => ({ ...prevState, profileImage: dataURL }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const memberId = getSelectedMemberId() || selectedMemberId || userProfile.id;
    
    if (!memberId) {
      toast.error("Member ID not found. Please refresh the page and try again.");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", memberId);
    formData.append("f_name", memberData.firstName);
    formData.append("l_name", memberData.lastName);
    formData.append("phone", memberData.phoneNumber);
    formData.append("email", memberData.email);
    formData.append("address", memberData.address);
    formData.append(
      "dob",
      memberData.dateOfBirth ? formatDate(memberData.dateOfBirth) : null
    );

  formData.append("profession", memberData.profession);
  formData.append("nin", memberData.NIN);
  formData.append("location_id", memberData.location_id ? parseInt(memberData.location_id) : "");


    // Convert Data URL to File and append
    if (profileImage) {
      const profileImageFile = dataURLtoFile(profileImage, "profile-image.png");
      if (profileImageFile) {
        formData.append("profile_image", profileImageFile);
      }
    }
    try {
      const response = await axiosInstance.post(
        "/update-user-details",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setEditSuccess(true);
      fetchMemberData(memberId);
      handleCloseModals();
    } catch (error) {
      let message = "An error occurred while updating user details.";
      if (error.response && error.response.data) {
        if (typeof error.response.data === "string") {
          message = error.response.data;
        } else if (error.response.data.message) {
          message = error.response.data.message;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        }
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
      console.error("Error updating user details:", error.response ? error.response.data : error.message);
    }
  };

  const handleSave2 = async () => {
    const memberId = getSelectedMemberId() || selectedMemberId || userProfile.id;
    
    if (!memberId) {
      toast.error("Member ID not found. Please refresh the page and try again.");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", memberId);
    formData.append("weight", memberData.weight);
    formData.append("height", memberData.height);
    formData.append("body_size", parseInt(memberData.bodySize));

    try {
      const response = await axiosInstance.post(
        "/update-user-stats",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setEdit2Success(true);
      fetchMemberData(memberId);
      handleCloseModals();
      removeAdminProfileData();
    } catch (error) {
      console.error(
        "Error updating user stats:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const totalPages = Math.ceil(subscriptionHistory.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = subscriptionHistory.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handleCloseModals = () => {
    setDeleteModalOpen(false);
    setDisableModalOpen(false);
    setEnableModalOpen(false);
    setEditModalOpen(false);
    setIsEditModal2Open(false);
  };

  const handleDeleteUser = async () => {
    const storedMemberId = getSelectedMemberId() || selectedMemberId || userProfile.id;
    
    if (!storedMemberId) {
      toast.error("Member ID not found. Please refresh the page and try again.");
      return;
    }

    try {
      const response = await axiosInstance.post("/delete-user", {
        user_id: storedMemberId,
      });
      handleCloseModals();
      setDeleteSuccess(true);
    } catch (error) {
      console.error(
        "Error deleting user:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const handleDisableUser = async () => {
    const storedMemberId = getSelectedMemberId() || selectedMemberId || userProfile.id;
    
    if (!storedMemberId) {
      toast.error("Member ID not found. Please refresh the page and try again.");
      return;
    }

    try {
      const response = await axiosInstance.post("/disable-user-access", {
        user_id: storedMemberId,
      });
      handleCloseModals();
      setDisableSuccess(true);
    } catch (error) {
      console.error(
        "Error disabling user:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const handleEnableUser = async () => {
    const storedMemberId = getSelectedMemberId() || selectedMemberId || userProfile.id;
    
    if (!storedMemberId) {
      toast.error("Member ID not found. Please refresh the page and try again.");
      return;
    }

    try {
      const response = await axiosInstance.post("/enable-user-access", {
        user_id: storedMemberId,
      });
      handleCloseModals();
      setEnableSuccess(true);
    } catch (error) {
      console.error(
        "Error Enabling user:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const getHighlightedDates = (attendanceHistory) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toLocaleDateString("en-CA");

    return attendanceHistory
      .map((attendance) => {
        const date = new Date(attendance.created_at);
        date.setHours(0, 0, 0, 0);
        const formattedDate = date.toLocaleDateString("en-CA");

        return formattedDate;
      })
      .filter((date) => date !== todayString);
  };

  const highlightedDates = useMemo(
    () => getHighlightedDates(attendanceHistory),
    [attendanceHistory]
  );

  const tileClassName = ({ date }) => {
    const dateString = date.toLocaleDateString("en-CA");
    return highlightedDates.includes(dateString) ? "highlighted-date" : null;
  };

  const tileContent = ({ date }) => {
    const dateString = date.toLocaleDateString("en-CA");
    return highlightedDates.includes(dateString) ? (
      <span>{date.getDate()}</span>
    ) : null;
  };

  return (
    <div className="dashboard">
      <div className="Nav">
        <Sidebar />
      </div>
      <div className="dashboard-display">
        <div className="top-bar container-fluid">
          <div
            className="top-bar-info"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "auto",
            }}
          >
            <Link to="/member-management">
              <img
                src={`${process.env.PUBLIC_URL}/images/arrow-left.png`}
                alt="Back"
              />
            </Link>
            <p style={{ margin: 0, fontWeight: "bold" }}>User Profile</p>
          </div>
          <div className="top-bar-button user-profile-btn">
            {userProfile.access_status === 0 && (
              <button onClick={() => setEnableModalOpen(true)}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/enable-user.png`}
                  alt="Enable Access"
                />{" "}
                Enable Access
              </button>
            )}
            {userProfile.access_status === 1 && (
              <button onClick={() => setDisableModalOpen(true)}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/user-minus.png`}
                  alt="Disable access"
                />{" "}
                Disable Access
              </button>
            )}
            <button
              style={{ color: "red" }}
              onClick={() => setDeleteModalOpen(true)}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/staff-delete.png`}
                alt="Delete"
              />{" "}
              Delete Profile
            </button>
          </div>
        </div>
        <div className="user-profile-display">
          <div className="member-details">
            <div className="add-member-head">
              <h2>Member Details</h2>
              <button onClick={() => setEditModalOpen(true)}>
                Edit member details
              </button>
            </div>

            <div className="member-preview-head">
              {isLoading ? (
                <img
                  style={{ borderRadius: "50%", border: "1px solid #E6E6E6" }}
                  src={`${process.env.PUBLIC_URL}/images/settings-avatar.png`}
                  alt="Loading..."
                  width="48px"
                  height="48px"
                />
              ) : (
                <img
                  style={{ borderRadius: "50%", border: "1px solid #E6E6E6" }}
                  src={
                    userProfile.profile_image ||
                    `${process.env.PUBLIC_URL}/images/settings-avatar.png`
                  }
                  alt="Profile"
                  width="48px"
                  height="48px"
                />
              )}
              <p>
                {isLoading
                  ? "..."
                  : `${userProfile.f_name || "N/A"} ${
                      userProfile.l_name || "N/A"
                    }`}
              </p>
            </div>

            <p
              style={{
                marginLeft: "23px",
                marginBottom: "4px",
                marginTop: "24px",
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              Personal Information
            </p>

            <div
              style={{ marginTop: "0" }}
              className="details-container container"
            >
              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_dob.png`}
                    alt="dob"
                  />
                  <p>Date of Birth</p>
                </span>
                <p className="D">
                  {isLoading ? "..." : userProfile.dob || "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_address.png`}
                    alt="Home Address"
                  />
                  <p style={{ minWidth: "100px" }}>Home Address</p>
                </span>
                <div className="address-container">
                  <Tippy content={userProfile.address}>
                    <p className="address-text">
                      {isLoading ? "..." : userProfile.address || "N/A"}
                    </p>
                  </Tippy>
                </div>
              </div>
             <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/location.png`}
                    alt="dob"
                  />
                  <p>Location</p>
                </span>
                <p className="D">
                  {isLoading || locationsLoading
                    ? "..."
                    : (() => {
                        if (!userProfile.location_id) return "N/A";
                        const loc = locations.find(l => l.id === userProfile.location_id);
                        return loc ? loc.name : userProfile.location_id;
                      })()}
                </p>
              </div>
              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_profession.png`}
                    alt="dob"
                  />
                  <p>Profession</p>
                </span>
                <p className="D">
                  {isLoading ? "..." : userProfile.profession || "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_email.png`}
                    alt="dob"
                  />
                  <p>Email</p>
                </span>
                <p className="D">
                  {isLoading ? "..." : userProfile.email || "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_mobile.png`}
                    alt="dob"
                  />
                  <p>Phone Number</p>
                </span>
                <p className="D">
                  {isLoading ? "..." : userProfile.phone || "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_nin.png`}
                    alt="dob"
                  />
                  <p>NIN</p>
                </span>
                <p className="D">
                  {isLoading ? "..." : userProfile.nin || "N/A"}
                </p>
              </div>
              {/* OTP display only if not null */}
              {userProfile.otp != null && userProfile.otp !== "" && (
                <div className="detail">
                  <span>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/user_nin.png`}
                      alt="OTP icon"
                    />
                    <p>OTP</p>
                  </span>
                  <p className="D">
                    {isLoading ? "..." : userProfile.otp}
                  </p>
                </div>
              )}
              {/* end OTP display */}
            </div>

            <p
              style={{
                marginLeft: "23px",
                marginBottom: "4px",
                marginTop: "24px",
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              Gym Information
            </p>

            <div
              style={{ marginTop: "0" }}
              className="details-container container"
            >
              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_weight.png`}
                    alt="dob"
                  />
                  <p>Start Weight</p>
                </span>
                <p className="D">
                  {isLoading
                    ? "..."
                    : userProfile.start_weight
                    ? `${userProfile.start_weight}kg`
                    : "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_nin.png`}
                    alt="dob"
                  />
                  <p>Start Height</p>
                </span>
                <p className="D">
                  {isLoading
                    ? "..."
                    : userProfile.height
                    ? `${userProfile.height}cm`
                    : "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_size.png`}
                    alt="dob"
                  />
                  <p>Body Size</p>
                </span>
                <p className="D">
                  {isLoading
                    ? "..."
                    : userProfile.body_size
                    ? `Size ${userProfile.body_size}`
                    : "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_sub_status.png`}
                    alt="dob"
                  />
                  <p>Subscription Status</p>
                </span>
                <p className="D">
                  {isLoading ? "..." : userProfile.subscription_status || "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_sub_plan.png`}
                    alt="dob"
                  />
                  <p>Subscription Plan</p>
                </span>
                <p className="D">
                  {isLoading ? "..." : userProfile.subscription_plan || "N/A"}
                </p>
              </div>

              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_sub_start.png`}
                    alt="dob"
                  />
                  <p>Subscription Start Date</p>
                </span>
                <p className="D">
                  {isLoading
                    ? "..."
                    : userProfile.subscription_start_date
                    ? format(
                        parseISO(userProfile.subscription_start_date),
                        "dd MMM yyyy"
                      )
                    : "N/A"}
                </p>
              </div>
              <div className="detail">
                <span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/user_sub_end.png`}
                    alt="dob"
                  />
                  <p>Subscription End Date</p>
                </span>
                <p className="D">
                  {isLoading
                    ? "..."
                    : userProfile.subscription_due_date
                    ? format(
                        parseISO(userProfile.subscription_due_date),
                        "dd MMM yyyy"
                      )
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="gym-information">
            <div className="add-member-head">
              <h2>Member Gym Attendance</h2>
              <button onClick={() => setIsEditModal2Open(true)}>
                Update member stats
              </button>
            </div>

            <p id="gym-stat-head">Gym Information</p>

            <div className="user-stat">
              <div className="user-stat-r1">
                <div className="stat-box">
                  <span>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/present-weight.png`}
                      alt="weight"
                    />
                    <p>Present weight</p>
                  </span>
                  <span>
                    <h3>
                      {isLoading
                        ? "..."
                        : userProfile.weight
                        ? `${userProfile.weight}kg`
                        : "N/A"}
                    </h3>
                  </span>
                </div>

                <div className="stat-box">
                  <span>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/present-height.png`}
                      alt="height"
                    />
                    <p>Present height</p>
                  </span>
                  <span>
                    <h3>
                      {isLoading
                        ? "..."
                        : userProfile.height
                        ? `${userProfile.height}cm`
                        : "N/A"}
                    </h3>
                  </span>
                </div>

                <div className="stat-box">
                  <span>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/present-body-size.png`}
                      alt="body size"
                    />
                    <p>Present body size</p>
                  </span>
                  <span>
                    <h3>
                      {isLoading
                        ? "..."
                        : userProfile.body_size
                        ? `Size ${userProfile.body_size}`
                        : "N/A"}
                    </h3>
                  </span>
                </div>
              </div>

              <div className="user-stat-r2">
                <div className="stat-box">
                  <span>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/timer.png`}
                      alt="timer"
                    />
                    <p>Total Hours Spent</p>
                  </span>
                  <span>
                    <h3>
                      {isLoading ? "..." : userProfile.total_gym_hours || "N/A"}
                    </h3>
                  </span>
                </div>
              </div>

              <div className="member-calendar">
                <Calendar
                  tileClassName={tileClassName}
                  tileContent={tileContent}
                />
              </div>

              <div className="attendance-text">
                <div className="attendance-indicator">
                  <p>hii</p>
                </div>
                <p>Session Attended</p>
              </div>

              <div className="attendance-description-outer">
                <p>Monthly attendance breakdown</p>
                <div className="attendance-description">
                  <span className="description-inner">
                    <div className="attendance-indicator">
                      <p>hii</p>
                    </div>
                    <p>Session Attended</p>
                  </span>
                  <p>{attendanceCount}</p>
                </div>
              </div>

              <style jsx>{`
                .highlighted-date {
                  position: relative;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                  margin: 0;
                }
                .highlighted-date:before {
                  content: "";
                  position: absolute;
                  background-color: #e0f4fe;
                  border-radius: 50%;
                  width: 35px;
                  height: 90%;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  z-index: 1;
                }
                .highlighted-date > span {
                  position: absolute;
                  z-index: 2;
                  text-align: center;
                  padding: 0;
                  margin: 0;
                  color: #0473bd;
                  font-weight: bold;
                }
              `}</style>
            </div>
          </div>
        </div>

        <h2 id="history-head">Subscription History</h2>
        <div className="member-sub-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Invoice Number</th>
                <th>Amount</th>
                <th>Payment Date</th>
                <th>Subscription Duration</th>
                <th>Payment Method</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5">
                    <div className="spinner"></div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>No subscription history found.</td>
                </tr>
              ) : (
                currentItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.tnx_ref}</td>
                    <td>{item.amount}</td>
                    <td>
                      {userProfile.created_at
                        ? format(
                            parseISO(userProfile.created_at),
                            "dd MMM yyyy"
                          )
                        : "N/A"}
                    </td>
                    <td>{item.subscription_duration}</td>
                    <td>{item.payment_method}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <Pagination
            currentPageProp={currentPage}
            totalPageProp={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
            loading={loading}
          />
        </div>
      </div>
      <EditMemberModal
        isOpen={isEditModalOpen}
        handleCloseModals={handleCloseModals}
        profileImage={profileImage}
        handleFileChange={handleFileChange}
        memberData={memberData}
        handleInputChange={handleInputChange}
        handleDateChange={handleDateChange}
        handleSave={handleSave}
      />
      <EditMemberStatsModal
        isOpen={isEditModal2Open}
        handleCloseModals={handleCloseModals}
        memberData={memberData}
        handleInputChange={handleInputChange}
        handleSave2={handleSave2}
      />
      <AccessToggleModal
        isOpen={isDisableModalOpen}
        handleClose={handleCloseModals}
        userProfile={userProfile}
        onConfirm={handleDisableUser}
        mode="disable"
      />
      <AccessToggleModal
        isOpen={isEnableModalOpen}
        handleClose={handleCloseModals}
        userProfile={userProfile}
        onConfirm={handleEnableUser}
        mode="enable"
      />
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        handleClose={handleCloseModals}
        userProfile={userProfile}
        onConfirmDelete={handleDeleteUser}
      />
      <StatusModal
        isOpen={disableSuccess}
        onRequestClose={() => setDisableSuccess(false)}
        title="User Profile Disabled"
        reload={true}
      />
      <StatusModal
        isOpen={EnableSuccess}
        onRequestClose={() => setEnableSuccess(false)}
        title="Access Granted"
        reload={true}
      />
      <StatusModal
        isOpen={deleteSuccess}
        onRequestClose={() => setDeleteSuccess(false)}
        title="User Profile Deleted"
        redirectTo="/member-management"
      />
      <StatusModal
        isOpen={editSuccess}
        onRequestClose={() => setEditSuccess(false)}
        title="Profile Updated"
        description="Member profile has been updated successfully"
      />
      <StatusModal
        isOpen={edit2Success}
        onRequestClose={() => setEdit2Success(false)}
        title="Gym Stats Updated"
        description="Member Gym stat has been updated successfully"
      />
    </div>
  );
}
export default ViewMember;
